import { CalendarTodayOutlined, InfoOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import { FC, useRef, useState } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { useLocalization } from "../hooks/useLocalization";
import { CalendarPopover } from "../pages/company/components/CalendarPopover";
import {
  formatDateYMD,
  getDate,
  getMonth,
  getYear,
  now,
} from "../shared/utils/date";
import { Tooltip } from "./Tooltip";

interface ExpirationDatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  label?: string;
  maxDate?: Date | null;
  isExternal?: boolean;
  checked: boolean;
  setChecked: (checked: boolean) => void;
  mobileLabel?: string;
  disabled?: boolean;
  showCompactView?: boolean;
  showTooltip?: boolean;
}

const dateNow = now();

export const ExpirationDatePicker: FC<ExpirationDatePickerProps> = ({
  value,
  onChange,
  label,
  maxDate,
  isExternal = false,
  checked,
  setChecked,
  mobileLabel,
  disabled = false,
  showCompactView = false,
  showTooltip = false,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const calendarRef = useRef<HTMLDivElement | null>(null);

  const isCompactView = useIsMobile() || showCompactView;

  const displayedCheckedState = isCompactView ? !checked : checked;

  const defaultLabel = label ?? t("components.expirationDatePicker.label");
  const defaultMobileLabel =
    mobileLabel ?? t("components.expirationDatePicker.mobileLabel");

  const handleClick = () => {
    setIsCalendarOpen(true);
  };

  const handleClose = () => {
    setIsCalendarOpen(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(isCompactView ? !event.target.checked : event.target.checked);
    handleClose();
  };

  const handleApply = (date: Date | null) => {
    if (date === null) {
      onChange(null);

      return;
    }

    const endOfDayDate = new Date(
      Date.UTC(getYear(date), getMonth(date), getDate(date), 23, 59, 59),
    );
    onChange(endOfDayDate);
  };

  const formatDate = (date: Date | null): string => {
    if (checked && maxDate) return formatDateYMD(maxDate);
    if (checked) return "∞";
    if (!date) return t("components.expirationDatePicker.selectDate");

    return formatDateYMD(date);
  };

  const switchLabel = isExternal
    ? t("components.expirationDatePicker.setByLicense")
    : t("components.expirationDatePicker.unlimited");
  const labelText = isCompactView ? defaultMobileLabel : switchLabel;

  const getLabelColor = () => {
    if (disabled) return theme.palette.text.disabled;

    return theme.colors.text[displayedCheckedState ? 7 : 0];
  };

  return (
    <>
      <Stack spacing={1} sx={{ mt: "24px" }}>
        {!isCompactView && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 400,
                color: theme.colors.text[1],
              }}
            >
              {defaultLabel}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: isCompactView ? "column" : "row",
            alignItems: isCompactView ? "flex-start" : "center",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={displayedCheckedState}
                  disabled={disabled}
                  onChange={handleChange}
                />
              }
              label={labelText}
              labelPlacement={isCompactView ? "start" : "end"}
              sx={{
                marginLeft: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: 12,
                  fontWeight: 500,
                  color: getLabelColor(),
                  width: isCompactView ? "auto" : 90,
                  ml: isCompactView ? 0 : 1,
                  mr: isCompactView ? 1 : 0,
                },
              }}
            />
            {showTooltip && (
              <Tooltip
                message={t("components.expirationDatePicker.tooltip")}
                placement="right"
              >
                <InfoOutlined
                  sx={{
                    fontSize: 18,
                    color: theme.colors.secondary[6],
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            )}
          </Box>
          <Button
            disabled={checked}
            endIcon={<CalendarTodayOutlined />}
            onClick={handleClick}
            onMouseDown={(e) => e.stopPropagation()}
            ref={anchorEl}
            sx={{
              width: "100%",
              justifyContent: "space-between",
              bgcolor: theme.colors.grey,
              color: theme.colors.text[6],
              borderRadius: "16px",
              height: 48,
              border: "none",
              textTransform: "none",
              fontWeight: 500,
              px: "18px",
              "&.Mui-disabled": {
                border: "none",
                transition: "none",
              },
              "&:hover": {
                border: "none",
                bgcolor: theme.colors.secondary[1],
              },
            }}
            variant="outlined"
          >
            {formatDate(value)}
          </Button>
        </Box>
      </Stack>

      <CalendarPopover
        anchorEl={anchorEl.current}
        calendarRef={calendarRef}
        enableFuture={true}
        maxDate={maxDate}
        minDate={dateNow}
        onApply={handleApply}
        onClose={handleClose}
        open={isCalendarOpen}
        value={value}
      />
    </>
  );
};
